import type { Permission } from '@/shared/DriveTypes';
import {
  Avatar,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  type SelectChangeEvent,
} from '@mui/material';
import { useState } from 'react';
import { Link } from 'wouter';
import Title from '~/elements/Title/Title';
import * as DriveAPI from '~/utils/DriveAPI';
import { useAuth, useContacts, useFile, useWikis } from '~/utils/hooks/hooks';
import { reg } from '~/utils/utils';
import { AvatarMenu } from '../AvatarMenu/AvatarMenu';
import * as formStyles from '../Form.css';
import * as styles from './Invite.css';

type PermissionType = 'fileOrganizer' | 'writer' | 'reader' | 'existing';

const blacklistedPatterns = [
  /noreply/i,
  /no-reply/i,
  /do-not-reply/i,
  /donotreply/i,
  /notification/i,
  /^unsubscribe/i,
  /^reply-/i,
  /email-support\.uber\.com$/i,
  /sale\.craigslist\.org$/i,
  /\.zendesk\.com$/i,
  /\.intercom-mail\.com$/i,
  /reply\.odesk\.com$/i,
  /reply\.airbnb\.com$/i,
  /reply\.github\.com$/i,
  /leave\.email\.vimeo\.com$/i,
  /reply\.drifttmail\.com$/i,
];

const isBlacklistedEmail = (email: string | undefined) =>
  email && blacklistedPatterns.some((pattern) => pattern.test(email));

type Contact = {
  id: string;
  name: string | undefined;
  email: string | undefined;
  photoURL: string | undefined;
};

const getInitialPermissionType = (): PermissionType | null => {
  const params = new URLSearchParams(window.location.search);
  const type = params.get('type');
  if (type === 'fileOrganizer' || type === 'writer' || type === 'reader' || type === 'existing') {
    return type;
  }
  return null;
};

type Props = {
  wikiId: string;
};

export const Invite = ({ wikiId }: Props) => {
  const { data: wiki } = useFile(wikiId, []);
  const { data: user } = useAuth();
  const { data: wikis = [] } = useWikis();
  const isSharedDrive = !!wiki?.driveId;
  const organizerType: PermissionType = isSharedDrive ? 'fileOrganizer' : 'writer';

  const [permissionType, setPermissionType] = useState<PermissionType | null>(getInitialPermissionType);
  const [searchInput, setSearchInput] = useState('');
  const [sent, setSent] = useState(false);
  const [lastSent, setLastSent] = useState<string[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
  const [permissionModalVisible, setPermissionModalVisible] = useState(false);
  const [usersRequiringPermission, setUsersRequiringPermission] = useState<string[]>([]);

  const effectivePermissionType = permissionType ?? organizerType;

  const { data: apiContacts, isLoading: _isLoadingContacts } = useContacts(true);
  const isLoadingContacts = _isLoadingContacts && apiContacts?.length === 0;

  const allWikiPermissions = [...(wiki?.permissions ?? []), ...wikis.flatMap((w) => w.permissions ?? [])];
  const permissionContacts: Contact[] = allWikiPermissions
    .filter((p): p is Permission & { type: 'user' | 'group' } => p.type === 'user' || p.type === 'group')
    .map((p) => ({
      id: `permission-${p.emailAddress}`,
      name: p.displayName,
      email: p.emailAddress,
      photoURL: p.photoLink,
    }));

  const contacts = [...permissionContacts, ...(apiContacts ?? [])]
    .filter((contact) => contact.email !== user?.email)
    .filter((contact) => contact.email !== 'wiki@youneedawiki.com')
    .filter((contact, index, arr) => arr.findIndex((c) => c.email === contact.email) === index);

  const handlePermissionChange = (event: SelectChangeEvent) => {
    const value = event.target.value as PermissionType;
    setPermissionType(value);
    window.history.replaceState(null, '', `/app/invite/${wikiId}?type=${value}`);
  };

  const isValidEmail = reg.regex.isEmail.test(searchInput);
  const typedEmailId = `typed-${searchInput}`;
  const hasSelectedContacts = selectedContacts.size > 0;
  const isEnabled = (isValidEmail || hasSelectedContacts) && !isSending;

  const getEmailsToInvite = () => {
    const selectedEmails = contacts.filter((c) => selectedContacts.has(c.id) && c.email).map((c) => c.email as string);
    const hasTypedEmailSelected = selectedContacts.has(typedEmailId);
    if (hasTypedEmailSelected) {
      return [searchInput, ...selectedEmails];
    }
    return isValidEmail ? [searchInput, ...selectedEmails] : selectedEmails;
  };

  const handleSubmit = async (e?: React.FormEvent, skipPermissionCheck = false) => {
    if (e) e.preventDefault();
    if (!isEnabled && !skipPermissionCheck) return;

    const emails = getEmailsToInvite();
    setIsSending(true);

    try {
      if (effectivePermissionType === 'existing' && !skipPermissionCheck) {
        const existingEmails = allWikiPermissions
          .filter((p): p is Permission & { emailAddress: string } => 'emailAddress' in p)
          .map((p) => p.emailAddress);
        const missingPermissions = emails.filter((email) => !existingEmails.includes(email));

        if (missingPermissions.length > 0) {
          setUsersRequiringPermission(missingPermissions);
          setPermissionModalVisible(true);
          setIsSending(false);
          return;
        }
      }

      const effectiveRole = effectivePermissionType === 'existing' ? 'reader' : effectivePermissionType;

      let successfulEmails = emails;
      if (effectivePermissionType !== 'existing') {
        const results = await Promise.allSettled(
          emails.map((email) =>
            DriveAPI.permissions
              .create({
                fileId: wikiId,
                role: effectiveRole,
                type: 'user',
                emailAddress: email,
                sendNotificationEmail: false,
              })
              .then(() => email)
          )
        );
        successfulEmails = results
          .filter((r): r is PromiseFulfilledResult<string> => r.status === 'fulfilled')
          .map((r) => r.value);
      }

      if (successfulEmails.length > 0) {
        await fetch('/api/invite', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ emails: successfulEmails, wikiId, role: effectiveRole, wikiName: wiki?.name }),
        });
      }

      setLastSent(successfulEmails);
      setSent(true);
      setSearchInput('');
      setSelectedContacts(new Set());
      setPermissionModalVisible(false);
      setUsersRequiringPermission([]);
    } finally {
      setIsSending(false);
    }
  };

  const handleContinueWithoutPermission = () => {
    setPermissionModalVisible(false);
    void handleSubmit(undefined, true);
  };

  if (isSending) {
    return (
      <div className={formStyles.container}>
        <CircularProgress />
      </div>
    );
  }

  if (sent) {
    return (
      <div className={formStyles.container}>
        <header className={formStyles.header}>{user && <AvatarMenu user={user} />}</header>
        <div className={formStyles.form}>
          <Title>Sent!</Title>
          <p className={formStyles.description}>The following people were sent an invite:</p>
          <ul className={styles.sentList}>
            {lastSent.map((email) => (
              <ListItem key={email} className={styles.sentListItem}>
                <ListItemAvatar>
                  <Avatar className={styles.avatar}>{email.slice(0, 1).toUpperCase()}</Avatar>
                </ListItemAvatar>
                <ListItemText primary={email} />
              </ListItem>
            ))}
          </ul>
          <div className={formStyles.actions}>
            <Link href={`/app/page/${wikiId}`} className={styles.doneButton}>
              Done
            </Link>
            <button
              type="button"
              className={formStyles.cancelButton}
              onClick={() => {
                setSent(false);
                setLastSent([]);
              }}
            >
              Invite more
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={formStyles.container}>
      <header className={formStyles.header}>
        <Link href="/app" className={styles.cancelLink}>
          Cancel
        </Link>
        {user && <AvatarMenu user={user} />}
      </header>
      <form className={formStyles.form} onSubmit={handleSubmit}>
        <Title>Invite</Title>
        <p className={formStyles.description}>Give people access to your project.</p>

        <FormControl className={styles.selectRoot}>
          <Select value={effectivePermissionType} onChange={handlePermissionChange} className={formStyles.select}>
            <MenuItem value={organizerType}>
              <ListItemText primary="Can Organize" secondary="Can organize, add and edit." />
            </MenuItem>
            <MenuItem value="reader">
              <ListItemText primary="Can Read" secondary="Can read only." />
            </MenuItem>
            <MenuItem value="existing">
              <ListItemText primary="Current permissions" secondary="Use existing permissions in Drive" />
            </MenuItem>
          </Select>
        </FormControl>

        {effectivePermissionType === 'existing' && (
          <div className={styles.existingPermissionInfo}>
            <span>Confirm the user has access in Drive: </span>
            <a
              href={`https://drive.google.com/drive/folders/${wikiId}`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.viewInDriveLink}
            >
              View in
              <img
                src="https://www.gstatic.com/images/branding/product/1x/drive_48dp.png"
                alt="Drive"
                className={styles.driveLogo}
              />
            </a>
            <ul className={styles.manageAccessInstructions}>
              <li>
                Click the icon{' '}
                <svg width="18" height="18" viewBox="0 0 24 24" focusable="false">
                  <path
                    fill="rgb(60,64,67)"
                    d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"
                  />
                </svg>{' '}
                or <span className={styles.manageAccessButton}>Manage access</span>
              </li>
            </ul>
          </div>
        )}

        <input
          autoFocus
          type="search"
          className={styles.searchInput}
          placeholder="Search a person or type an email..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          autoComplete="off"
        />

        {isLoadingContacts && <CircularProgress size={24} />}

        {(() => {
          const filteredContacts = contacts
            .filter((contact) => !isBlacklistedEmail(contact.email))
            .filter((contact) => {
              if (!searchInput) return true;
              const searchLower = searchInput.toLowerCase();
              return (
                contact.name?.toLowerCase().includes(searchLower) || contact.email?.toLowerCase().includes(searchLower)
              );
            });

          const hasNoResults = !isLoadingContacts && filteredContacts.length === 0;

          if (hasNoResults && !isValidEmail) {
            const contactsUrl = searchInput
              ? `https://contacts.google.com/search/${encodeURIComponent(searchInput)}`
              : 'https://contacts.google.com/frequent';
            return (
              <p className={styles.noResults}>
                No results found.{' '}
                <a href={contactsUrl} target="_blank" rel="noopener noreferrer">
                  Add to contacts
                </a>
              </p>
            );
          }

          if (hasNoResults && isValidEmail) {
            return (
              <List className={styles.contactsList}>
                <ListItemButton
                  className={styles.listItemButton}
                  onClick={() => {
                    setSelectedContacts((prev) => {
                      const next = new Set(prev);
                      if (next.has(typedEmailId)) {
                        next.delete(typedEmailId);
                      } else {
                        next.add(typedEmailId);
                      }
                      return next;
                    });
                  }}
                >
                  <ListItemAvatar>
                    <Avatar className={styles.avatar}>{searchInput.slice(0, 1).toUpperCase()}</Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={searchInput} />
                  <Checkbox
                    className={styles.checkbox}
                    checked={selectedContacts.has(typedEmailId)}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemButton>
              </List>
            );
          }

          if (filteredContacts.length > 0) {
            return (
              <List className={styles.contactsList}>
                {filteredContacts.map((contact) => (
                  <ListItemButton
                    className={styles.listItemButton}
                    key={contact.id}
                    onClick={() => {
                      setSelectedContacts((prev) => {
                        const next = new Set(prev);
                        if (next.has(contact.id)) {
                          next.delete(contact.id);
                        } else {
                          next.add(contact.id);
                        }
                        return next;
                      });
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar {...(contact.photoURL && { src: contact.photoURL })}>
                        {(contact.name ?? contact.email)?.slice(0, 1).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={contact.name ?? contact.email} secondary={contact.email} />
                    <Checkbox
                      className={styles.checkbox}
                      checked={selectedContacts.has(contact.id)}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemButton>
                ))}
              </List>
            );
          }

          return null;
        })()}

        <div className={formStyles.actions}>
          <button type="submit" className={formStyles.primaryButton} disabled={!isEnabled}>
            Invite
          </button>
          <Link href={`/app/page/${wikiId}`} className={formStyles.cancelButton}>
            Skip
          </Link>
        </div>
      </form>

      <Dialog open={permissionModalVisible} onClose={() => setPermissionModalVisible(false)}>
        <DialogTitle>Permissions required</DialogTitle>
        <DialogContent>
          <p>The following invited users do not have access to the wiki folder.</p>
          <ul>
            {usersRequiringPermission.map((email) => (
              <li key={email}>{email}</li>
            ))}
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermissionModalVisible(false)}>Cancel</Button>
          <Button onClick={handleContinueWithoutPermission} variant="contained">
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
